import {
  invoicesTransport,
  INVOICES_BASE_URL,
  invoiceRequest,
} from "@/src/config/invoicesTransport.js";

export default {
  async recalculate(invoiceId) {
    return invoiceRequest("post", `/invoices/records/${invoiceId}/recalculate`);
  },

  async send(invoiceId, { toEmail, senderName, organisationName } = {}) {
    const body = {};
    if (toEmail) body.to_email = toEmail;
    if (senderName) body.sender_name = senderName;
    if (organisationName) body.organisation_name = organisationName;
    return invoiceRequest("post", `/invoices/records/${invoiceId}/send`, body);
  },

  getPdfUrl(invoiceId) {
    return `${INVOICES_BASE_URL}/invoices/records/${invoiceId}/pdf`;
  },

  async fetchPdfBlobUrl(invoiceId) {
    try {
      const res = await invoicesTransport.get(
        `/invoices/records/${invoiceId}/pdf`,
        { responseType: "blob" },
      );
      return URL.createObjectURL(res.data);
    } catch (error) {
      console.error(`InvoiceService fetchPdfBlobUrl(${invoiceId}) failed:`, error);
      throw error;
    }
  },

  async listLineItems(invoiceId) {
    return invoiceRequest("get", `/invoices/records/${invoiceId}/line-items`);
  },

  async listAppliedPresets(invoiceId) {
    return invoiceRequest("get", `/invoices/records/${invoiceId}/applied-presets`);
  },

  async getCustomerEmails(invoiceId) {
    return invoiceRequest("get", `/invoices/records/${invoiceId}/customer-emails`);
  },

  async createLineItem(invoiceId, attributes) {
    return invoiceRequest(
      "post",
      `/invoices/records/${invoiceId}/line-items`,
      { attributes },
    );
  },

  async updateLineItem(invoiceId, lineItemId, attributes) {
    return invoiceRequest(
      "patch",
      `/invoices/records/${invoiceId}/line-items/${lineItemId}`,
      { attributes },
    );
  },

  async deleteLineItem(invoiceId, lineItemId) {
    return invoiceRequest(
      "delete",
      `/invoices/records/${invoiceId}/line-items/${lineItemId}`,
    );
  },

  async addDiscount(invoiceId, presetId) {
    return invoiceRequest(
      "post",
      `/invoices/records/${invoiceId}/discounts/${presetId}`,
    );
  },

  async removeDiscount(invoiceId, presetId) {
    return invoiceRequest(
      "delete",
      `/invoices/records/${invoiceId}/discounts/${presetId}`,
    );
  },

  async addFee(invoiceId, presetId) {
    return invoiceRequest(
      "post",
      `/invoices/records/${invoiceId}/fees/${presetId}`,
    );
  },

  async removeFee(invoiceId, presetId) {
    return invoiceRequest(
      "delete",
      `/invoices/records/${invoiceId}/fees/${presetId}`,
    );
  },

  async addTax(invoiceId, presetId) {
    return invoiceRequest(
      "post",
      `/invoices/records/${invoiceId}/taxes/${presetId}`,
    );
  },

  async removeTax(invoiceId, presetId) {
    return invoiceRequest(
      "delete",
      `/invoices/records/${invoiceId}/taxes/${presetId}`,
    );
  },

  async setPaymentTerm(invoiceId, paymentTermId) {
    return invoiceRequest(
      "put",
      `/invoices/records/${invoiceId}/payment-term`,
      { payment_term_id: paymentTermId || "" },
    );
  },

  async updateInvoice(invoiceId, { attributes = {}, relations = {} } = {}) {
    return invoiceRequest("patch", `/invoices/records/${invoiceId}`, {
      attributes,
      relations,
    });
  },

  async bulkUpdate(recordIds, attributes) {
    return invoiceRequest("post", `/invoices/records/bulk-update`, {
      record_ids: recordIds,
      attributes,
    });
  },

  async markPaid(invoiceId, { paidAt, paymentReference } = {}) {
    const body = {};
    if (paidAt) body.paid_at = paidAt;
    if (paymentReference) body.payment_reference = paymentReference;
    return invoiceRequest(
      "post",
      `/invoices/records/${invoiceId}/mark-paid`,
      body,
    );
  },

  async voidInvoice(invoiceId, { reason } = {}) {
    const body = {};
    if (reason) body.reason = reason;
    return invoiceRequest("post", `/invoices/records/${invoiceId}/void`, body);
  },

  async markUncollectible(invoiceId, { reason } = {}) {
    const body = {};
    if (reason) body.reason = reason;
    return invoiceRequest(
      "post",
      `/invoices/records/${invoiceId}/mark-uncollectible`,
      body,
    );
  },
};
